import React from 'react';
import { IdText } from './MaintenanceTriggerForm.slice';
import { AnnotationStandardDetails, FindingTypeDetails } from '../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import {
  restrictInputForDigitsAndSeparators,
  restrictInputFor2Decimals,
  ThousandSeparatorDotRegex,
  prepareDoubleValue,
  prepareIntValue
} from '../../AnnotationManager/Annotation/AnnotationForm/Utils';
import { Input, Select, Tooltip } from 'antd';
import styles from './MaintenanceTriggerForm.module.scss';

export class PropConstants {
  static ID: string = 'id';
  static FINDING_TYPE: string = 'findingType';
  static DEFECT_LOCATION: string = 'defectLocation';
  static BLADE_ZONE: string = 'BladeZone';
  static COMMENTS: string = 'comments';
  static RESPONSE_LEVEL_ACTION: string = 'responseLevel';
  static RESPONSE_LEVEL_DESCRIPTION: string = 'responseLevelDescription';
  static LAYER: string = 'Layer';
  static SEVERITY: string = 'Severity';
  static INDEX: string = 'index';
}

export const RequiredFields = [
  PropConstants.FINDING_TYPE,
  PropConstants.RESPONSE_LEVEL_ACTION,
  PropConstants.RESPONSE_LEVEL_DESCRIPTION,
  PropConstants.LAYER, // only work for SGRE standard
  PropConstants.SEVERITY
];

export const notItemsColumns = [
  PropConstants.INDEX,
  PropConstants.FINDING_TYPE,
  PropConstants.DEFECT_LOCATION,
  PropConstants.RESPONSE_LEVEL_ACTION,
  PropConstants.RESPONSE_LEVEL_DESCRIPTION,
  PropConstants.BLADE_ZONE
];

export const tresholdColumns: IdText[] = [
  {
    id: 'AbsLongitudinalLengthMM',
    text: 'Abs. Longitudinal Length [mm]'
  },
  {
    id: 'AbsTransverseLengthMM',
    text: 'Abs. Transverse Length [mm]'
  },
  {
    id: 'RelLengthPerM',
    text: 'Rel. Length per [m] %'
  },
  {
    id: 'RelAreaPerM2',
    text: 'Rel. Area per [m2] %'
  },
  {
    id: 'AbsLengthMM',
    text: 'Abs. Length [mm]'
  },
  {
    id: 'AbsAreaM2',
    text: 'Abs. Area [m2]'
  }
];

export const commonColumns: IdText[] = [
  {
    id: 'findingType',
    text: 'Finding Type'
  },
  {
    id: 'defectLocation',
    text: 'Blade Zone'
  },
  {
    id: 'comments',
    text: 'Comment'
  },
  {
    id: 'responseLevel',
    text: 'Response Level (Action)'
  },
  {
    id: 'responseLevelDescription',
    text: 'Response Level (Description)'
  }
];

export const defectLocations: IdText[] = [
  {
    id: ' ',
    text: ' '
  },
  {
    id: 'Leading Edge',
    text: 'Leading Edge'
  },
  {
    id: 'Trailing Edge',
    text: 'Trailing Edge'
  },
  {
    id: 'Standard Zone',
    text: 'Standard Zone'
  },
  {
    id: 'Outboard Zone',
    text: 'Outboard Zone'
  }
];

export const MaintenanceTriggerColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: '25%',
    minWidth: 120
  },
  {
    title: 'Default',
    dataIndex: 'default',
    key: 'default',
    render: (defaultValue: boolean) => (defaultValue ? <span>Yes</span> : <span>No</span>),
    width: '25%',
    minWidth: 120
  },
  {
    title: 'Annotation Standard',
    dataIndex: 'annotationStandardName',
    key: 'annotationStandardName',
    width: '25%',
    minWidth: 120
  },
  {
    title: 'Annotation Standard Version',
    dataIndex: 'annotationStandardVersion',
    key: 'annotationStandardVersion',
    width: '25%',
    minWidth: 120
  }
];

export const orderOfColumns = [
  'findingType',
  'defectLocation',
  'Layer',
  'Severity',
  'Observation',
  'Subcategory',
  'AbsLongitudinalLengthMM',
  'AbsTransverseLengthMM',
  'RelLengthPerM',
  'RelAreaPerM2',
  'AbsLengthMM',
  'AbsAreaM2',
  'comments',
  'responseLevel',
  'responseLevelDescription'
];

export const initialColumns = [...commonColumns, ...tresholdColumns];

export const getSelectedFindingTypeAndPopulateRule = (standard: AnnotationStandardDetails, updatedRule: any, key: string, value: string) => {
  const previousType = standard.findingTypes.find(x => x.id === updatedRule[key])!;
  if (previousType) {
    const previousFindingTypeColumns = previousType.findingTypeTemplate!.properties.map(x => ({
      id: x.name, //or id
      text: x.fullName
    }));

    previousFindingTypeColumns.forEach(column => {
      updatedRule[column.id] = undefined;
    });
  }

  const { selectedFindingType, findingTypeColumns } = getFindingTypeAndColumns(standard, value);

  updateRuleWithClearFields(findingTypeColumns, updatedRule);

  return { selectedFindingType, findingTypeColumns };
};

export const getFindingTypeAndColumns = (standard: AnnotationStandardDetails, id: string) => {
  const selectedFindingType = standard.findingTypes.find(x => x.id === id)!;
  const findingTypeColumns = selectedFindingType.findingTypeTemplate!.properties.map(x => ({
    id: x.name, //or id
    text: x.fullName
  }));

  return { selectedFindingType, findingTypeColumns };
};

export const updateRuleWithClearFields = (findingTypeColumns: IdText[], updatedRule: any) => {
  findingTypeColumns.forEach(column => {
    updatedRule[column.id] = '';
  });
};

export const updateRuleWithClearFieldsWhereEmpty = (findingTypeColumns: IdText[], updatedRule: any) => {
  findingTypeColumns.forEach(column => {
    if (!updatedRule[column.id]) {
      updatedRule[column.id] = '';
    }
  });
};

export const insertFindingTypeColumns = (columns: IdText[], columnsToAdd: IdText[]) => {
  columns.sort(function (a, b) {
    return orderOfColumns.indexOf(a.id.toString()) - orderOfColumns.indexOf(b.id.toString());
  });
  return columns
    .slice(0, 2)
    .concat(columnsToAdd.filter(x => x.text?.toLowerCase().trim().replace(' ', '') !== 'bladezone'))
    .concat(columns.slice(2));
};

const decimalNumberFields = ['RelLengthPerM', 'RelAreaPerM2', 'AbsAreaM2'];
const integerNumberFields = ['AbsLongitudinalLengthMM', 'AbsTransverseLengthMM', 'AbsLengthMM'];
const numberFields = [...integerNumberFields, ...decimalNumberFields];

export const isNumberField = (column: string | number) => numberFields.includes(column.toString());

export const isDecimalField = (column: string | number) => decimalNumberFields.includes(column.toString());

export const isIntField = (column: string | number) => integerNumberFields.includes(column.toString());

export const checkNumberValue = (column: string | number, value: string) => {
  if (isDecimalField(column) && restrictInputFor2Decimals(value)) {
    return true;
  }

  if (isIntField(column) && restrictInputForDigitsAndSeparators(value)) {
    return true;
  }
};

export const SelectInputProperty = (
  values: IdText[],
  key: string | number,
  value: string,
  onCellUpdated: (key: string | number, value: string) => void
) => (
  <Select
    value={value}
    showSearch
    style={{ width: 200 }}
    placeholder='Please select'
    optionFilterProp='children'
    onChange={e => onCellUpdated(key, e as string)}
    filterOption={true}
  >
    {values.map(type => (
      <Select.Option value={type.id} key={type.id}>
        {type.text}
      </Select.Option>
    ))}
  </Select>
);

export const InputProperty = (key: string | number, value: string, onCellUpdated: (key: string | number, value: string) => void, disabled = false) => {
  return isNumberField(key) ? CellNumberInput(value, key, onCellUpdated, disabled) : CellStringInput(value, key, onCellUpdated, disabled);
};

const CellNumberInput = (value: string, key: string | number, onCellUpdated: (key: string | number, value: string) => void, disabled = false) => {
  const isDecimal = isDecimalField(key);

  return (
    <Input
      className={styles.inputStyles}
      placeholder={isDecimal ? '0.00' : '0'}
      value={isDecimal ? prepareIntValue(value) : prepareDoubleValue(value)}
      disabled={disabled}
      onChange={e =>
        checkNumberValue(key, e.target.value) ? onCellUpdated(key, e.target.value.toString().replace(ThousandSeparatorDotRegex, '')) : ''
      }
    />
  );
};

const CellStringInput = (value: string, key: string | number, onCellUpdated: (key: string | number, value: string) => void, disabled = false) => {
  return (
    <Tooltip placement='top' title={key === 'responseLevelDescription' ? value : ''}>
      <Input className={styles.inputStyles} placeholder='Enter Value' value={value} disabled={disabled} onChange={e => onCellUpdated(key, e.target.value)} />
    </Tooltip>
  );
};

export const getAllStandardProperties = (standard: AnnotationStandardDetails) => {
  const allFTColumns = standard.findingTypes
    .map(x => x.findingTypeTemplate?.properties)
    .filter(x => x)
    .flat()
    .map(x => ({
      id: x!.name, //or id
      text: x!.fullName
    }));
  const uniqueFTColumns = Array.from(new Set(allFTColumns.map(column => column.id)))
    .map(id => allFTColumns.find(column => column.id === id)!)
    .filter(x => x);

  return uniqueFTColumns;
};
